/**
 * @file 画面に入ったら true になる hook (1 回きり)。
 *
 * @what: IntersectionObserver を callback ref で張り、200px 手前で true にする。
 * @why: サムネイルや誌面の埋め込みを、開いた瞬間に全部取りに行かないため。
 * @failure: IntersectionObserver が無い環境 (テスト等) では最初から true。
 *   unmount 時は callback ref が null で呼ばれ、観測をやめる。
 */

import { useCallback, useRef, useState } from "react";

export function useInView<T extends Element>(): [(el: T | null) => void, boolean] {
  const [inView, setInView] = useState(typeof IntersectionObserver === "undefined");
  const obsRef = useRef<IntersectionObserver | null>(null);
  const attach = useCallback(
    (el: T | null) => {
      obsRef.current?.disconnect();
      obsRef.current = null;
      if (!el || inView || typeof IntersectionObserver === "undefined") return;
      const obs = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setInView(true);
            obs.disconnect();
          }
        },
        { rootMargin: "200px" },
      );
      obs.observe(el);
      obsRef.current = obs;
    },
    [inView],
  );
  return [attach, inView];
}
